import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { SettingsService, ClockHourMode, ScrollHideMode, FirstDayOfWeek } from 'services/settings.service';
import { LoggingService } from 'services/logging.service';
import { ReviewPromptService } from 'services/review-prompt.service';
import { canShowStoreReviewPrompt, requestAppReviewAsync } from 'services/cs-review-prompt';
import { FoodDbService } from 'services/food-db.service';
import { AuthService } from 'services/auth.service';
import { CommonDbService } from 'services/common-db.service';
import { ENGLISH_TEXT_SEGMENT, textSegmentForLocale } from 'services/text-locale';
import { NutritionLabelStyle, DateFormat } from '@cf-shared/enums';
import { describeError } from '@cf-shared/error-describe';
import { CsCardListComponent } from 'components/cs-card-list/cs-card-list';
import { declareEvent, PubSubService } from 'services/pubsub.service';
import { LoadingController } from 'providers/loading-controller';
import { TelemetryService } from 'services/telemetry.service';
import { NotificationService } from 'services/notification.service';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { isCapacitorNative } from 'environments/environment-helpers';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { untranslatedString } from 'models/translated-string';
import { CS_UI_LANGUAGE_KEY, CS_UI_LANGUAGE_UID_KEY, CS_UI_LANGUAGE_UNCONFIRMED_KEY, encodeUnconfirmedChoice } from 'i18n/ui-language';
import { getMyApp } from 'app/app-interface';
import { isLocale, locales } from '../../../gensrc/i18n/i18n-util';


export const AppSettingsChangedEvent = declareEvent<{}>('app-settings:changed');

@Component({
    selector: 'page-app-settings',
    templateUrl: 'app-settings-page.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSettingsPage extends CsPage<{}> {
  protected LL = LL;
  protected dbContent: string;
  public NutritionLabelStyle = NutritionLabelStyle;
  public ClockHourMode = ClockHourMode;
  public DateFormat = DateFormat;
  public FirstDayOfWeek = FirstDayOfWeek;
  public ScrollHideMode = ScrollHideMode;
  public numCachedFoods: number;
  public isNative = isCapacitorNative();

  // Each language's own native name — the only per-locale data not already in
  // the canonical locale set. A locale with no entry here falls back to showing
  // its id (so a newly-added locale still appears, just unlocalised).
  private static readonly LANGUAGE_NATIVE_NAMES: Readonly<Record<string, string>> = {
    en: 'English',
    es: 'Español',
    nl: 'Nederlands',
    fr: 'Français',
  };

  // UI-language options for the Language picker. The option VALUES come from the
  // generated `locales` set (derived from supported-locales.json), so the picker
  // can never drift from the supported-locale list — only the display labels are
  // added per locale above.
  protected languageOptions: ReadonlyArray<{ value: string; label: string }> = locales.map((code) => ({
    value: code,
    label: AppSettingsPage.LANGUAGE_NATIVE_NAMES[code] ?? code,
  }));

  @ViewChild('cardList') cardList: CsCardListComponent;

  constructor(
    public navCtrl: NavController,
    public settings: SettingsService,
    private pubSub: PubSubService,
    private loadingCtrl: LoadingController,
    private foodDb: FoodDbService,
    private telemetry: TelemetryService,
    private cdRef: ChangeDetectorRef,
    private notificationService: NotificationService,
    private userDb: UserDb,
    private debug: DebugService,
    private cloudFunctions: CloudFunctionsService,
    private alertCtrl: AlertController,
    private reviewPrompt: ReviewPromptService,
    private logging: LoggingService,
    private commonDb: CommonDbService,
    private auth: AuthService,
  ) {
    super();

  }

  async ionViewCanEnter() {
    if (this.settings.locale.decimalSeparatorOverride == null) {
      this.settings.locale.decimalSeparatorOverride = ''; // this is needed for the UI
    }
  }

  async ionViewDidEnter() {
    await this.countFoods()
  }

  async countFoods() {
    this.numCachedFoods = await this.foodDb.countCachedFoodsAsync();
    this.safeMarkForCheck(this.cdRef);
  }

  async ionViewWillLeave() {
    await this.settings.saveAsync();
    this.pubSub.publish(AppSettingsChangedEvent, {});
  }


  handleUncacheFoods() {
    void this.uncacheFoodsAsync();
  }

  handleTestSurvey() {
    this.telemetry.testSurvey();
  }

  clearDebugLog() {
    this.settings.debugLog = '';
    this.settings.saveInBackground('cleared debug log');
    this.cdRef.markForCheck();
  }

  async uncacheFoodsAsync() {
    let loading = this.loadingCtrl.create({ content: LL.settings.foods.uncaching() });
    void loading.present();
    await this.foodDb.uncacheAllFoodsAsync();
    void this.countFoods();
    void loading.dismiss();
    this.safeMarkForCheck(this.cdRef);
  }

  /**
   * #4625 — fire the store's own review prompt right now, ignoring the gate.
   *
   * **Test mode only, and it must stay that way.** Apple's guidance is not to
   * call `requestReview` in response to somebody tapping something, and the
   * practical reason is the same: the operating system silently ignores the
   * request once an app has asked a few times in a year, so a button wired to it
   * does nothing and looks broken. That is exactly why the real Settings row is
   * a store LINK instead. This exists so the prompt can be SEEN on a device.
   */
  async handleShowReviewPrompt() {
    if (!canShowStoreReviewPrompt()) {
      await this.alertCtrl.showAlertAsync({
        message: untranslatedString(
          'There is no store review prompt on the web. Run this on a device.'),
        buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
      });
      return;
    }
    await requestAppReviewAsync(this.logging);
  }

  /**
   * #4625 — ask the gate what it would decide for this account, and say why.
   *
   * The gate refuses for a NAMED reason rather than a bare no, precisely so this
   * is answerable. Without it, "no prompt appeared" cannot be told apart from
   * "this person does not qualify", which is the most confusing thing about
   * testing this feature.
   */
  async handleExplainReviewPrompt() {
    let message: string;
    try {
      const decision = await this.reviewPrompt.decideAsync();
      const verdict = decision.mayPrompt
        ? 'This account WOULD be prompted at the next qualifying moment.'
        : `No prompt. Reason: ${decision.reason}`;
      // #4816: which audience is in force, and which rule let this account in
      const phase = `\n\nPhase: ${decision.phase}.`
        + (decision.tenureRule ? ` Tenure rule: ${decision.tenureRule}.` : '');
      const malformed = (decision.configMalformed
        ? '\n\nNote: the configured rollout percent could not be read, so it fell back to 100.'
        : '')
        + (decision.phaseMalformed
          ? '\n\nNote: the configured phase could not be read, so it fell back to 1.'
          : '');
      message = verdict + phase + malformed;
    } catch (err) {
      // deciding reads the config database and the workout history, so it can
      // fail. Showing the failure is the whole point of this tool: an
      // unguarded await would leave the button doing nothing at all, which is
      // exactly the "nothing happened and I cannot tell why" this exists to
      // remove. `considerPromptingAsync` guards the same call for its own
      // reason — there, so a failure cannot break the thing the person was
      // doing.
      message = `The gate could not be asked: ${describeError(err)}`;
    }
    // an explicit Ok, matching the Test Push Notification tool below. An alert
    // with no buttons can only be dismissed by tapping the backdrop, which is
    // not obvious for a dialog carrying a diagnostic the reader has to finish
    // reading first.
    await this.alertCtrl.showAlertAsync({
      message: untranslatedString(message),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }

  /**
   * #4816 — would the most recently finished workout count as a personal record?
   *
   * Answers only that question, ignoring the gate and the phase, so a browser
   * test can prove the record lookup works: the store prompt itself never
   * appears on the web. One line per exercise, so a "no" says which best it
   * did not beat.
   */
  async handleExplainPersonalRecord() {
    let message: string;
    // the first lookup on a device builds the best-strength index from every workout,
    // which took about 10 seconds on a big account (#6157); show that the app is busy
    const loading = this.loadingCtrl.create({ content: untranslatedString('Checking personal records…') });
    void loading.present();
    try {
      const rows = await this.reviewPrompt.explainLatestWorkoutPersonalRecordAsync();
      if (rows === undefined) {
        message = 'No finished workout yet.';
      } else {
        const beat = rows.some(row => row.beat);
        const lines = rows.map(row => `${row.exerciseId}: ${row.thisWorkout_net_kg.toFixed(1)} kg, earlier best `
          + (row.earlierBest_net_kg === undefined ? 'none' : `${row.earlierBest_net_kg.toFixed(1)} kg`)
          + (row.beat ? ' (record)' : ''));
        message = `Personal record: ${beat ? 'YES' : 'NO'}.\n\n${lines.join('\n')}`;
      }
    } catch (err) {
      // showing the failure is the point of this tool, same as the gate tool above
      message = `The record could not be checked: ${describeError(err)}`;
    } finally {
      // dismiss before the alert, so the alert is never hidden under the overlay
      await loading.dismiss();
    }
    await this.alertCtrl.showAlertAsync({
      message: untranslatedString(message),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }

  async handleTestNotification() {
    await this.notificationService.scheduleTestNotificationAsync(this.userDb, this.settings, this.debug.getNowLocal(), this.cloudFunctions);
    await this.alertCtrl.showAlertAsync({
      message: untranslatedString('Test push scheduled. It will arrive on all mobile devices within the next minute.'),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }

  /** Bring back every toast the user hid with "Never show again" (#3991).
   *
   * That button is otherwise a one-way door: it writes to the user's settings permanently, and
   * nothing anywhere in the app could undo it. An account could end up with (say) the offline toast
   * switched off with no memory of ever doing it, and no way back short of editing localStorage by
   * hand. Test mode only for now — the need is general, so this can be opened up later.
   *
   * Reports what it did, so tapping it when nothing is hidden does not look like a dead button. */
  async handleResetHiddenMessagesAsync() {
    const count = this.settings.dismissedToastCount();
    if (count === 0) {
      await this.alertCtrl.showAlertAsync({
        message: LL.settings.testSettings.noHiddenMessages(),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return;
    }

    await this.settings.resetDismissedToastsAsync();
    if (this.viewDestroyed) return;

    await this.alertCtrl.showAlertAsync({
      message: LL.settings.testSettings.hiddenMessagesRestored({ count }),
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  onCardAnimationsChange() {
    this.cardList.updateLayouts();
    this.cdRef.markForCheck();
  }

  /** Change the UI language.
   *
   * Writes BOTH the authoritative per-user preference (settings.uiLanguage,
   * which syncs to CouchDB and follows the user across devices) and the
   * device-scoped boot cache (cs_ui_language, which the i18n module reads
   * synchronously at load), then reloads the app so the module-level LL
   * rebinds to the new locale. A live re-render is not possible because LL is a
   * module-level const imported by value into ~136 files — reload is the
   * low-risk switch. See design-docs/i18n-translation.md.
   *
   * Sync-first (#4284): because `global_common` now syncs only English plus the
   * ACTIVE language, the new language's curated `$text/` documents are NOT on the
   * device yet at switch time. So they are pulled FIRST, with a progress state;
   * if that pull fails (offline, or a failed/timed-out download) the switch is
   * abandoned — the old language is kept and the user is told — rather than
   * reloading into a silently half-English app (the English fallback reports
   * nothing). English needs no pull, so switching TO English skips it. */
  async onUiLanguageChangeAsync(newLang: string) {
    // Ignore any value that is not a supported locale (defensive — the picker
    // only offers generated locale ids, but the event value is untyped). This
    // also narrows newLang to Locales for the tightened uiLanguage field.
    if (!isLocale(newLang)) return;
    // No-op if unchanged (picking English while already defaulting to English
    // must not trigger a reload). An empty stored value means "never chosen",
    // which the picker displays as English.
    if (newLang === (this.settings.uiLanguage || 'en')) return;

    // Sync the chosen language's curated content BEFORE anything is mutated, so a
    // failed download leaves the app exactly as it was. Show progress only while a
    // real pull runs — English is always resident, so switching TO it downloads
    // nothing and shows no spinner.
    const willDownload = textSegmentForLocale(newLang) !== ENGLISH_TEXT_SEGMENT;
    const loading = willDownload ? this.loadingCtrl.create({ content: LL.settings.downloadingLanguage() }) : null;
    if (loading) void loading.present();
    const languageReady = await this.commonDb.syncLanguageTextBeforeSwitchAsync(newLang);
    if (loading) void loading.dismiss();
    if (!languageReady) {
      // Keep the current language and tell the user. The alert renders in the
      // still-current language, which is correct — the switch never happened.
      const languageName = this.languageOptions.find(o => o.value === newLang)?.label ?? newLang;
      await this.alertCtrl.showAlertAsync({
        title: LL.settings.languageDownloadFailed.title(),
        message: LL.settings.languageDownloadFailed.message({ language: languageName }),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return;
    }

    // The new language is on the device — record the deliberate switch (now that
    // it will actually happen) BEFORE mutating settings, so `from` is the language
    // they were actually on. The reload teardown flushes telemetry, so this queued
    // event survives the reload below. Carries the raw browser / country language
    // context (#4147) so a switch can be analysed against what the device offered.
    const from = this.settings.uiLanguage || 'en';
    const languageContext = this.telemetry.getUiLanguageTelemetryProps();
    this.telemetry.trackEvent('language_changed', { from, to: newLang, ...languageContext });

    this.settings.uiLanguage = newLang;
    localStorage.setItem(CS_UI_LANGUAGE_KEY, newLang);
    // Record WHO chose it. The boot reconciliation needs this to tell "this
    // user's own recent choice, not yet synced" apart from "a language left
    // behind by a different user on a shared device" — without it, a settings
    // doc that has not caught up yet silently reverts this switch. See
    // decideUiLanguageReconcile in i18n/ui-language.ts.
    localStorage.setItem(CS_UI_LANGUAGE_UID_KEY, this.auth.user.uid);
    // Mark the choice unconfirmed until the settings doc comes back agreeing with
    // it. Until then it outranks whatever that doc says — otherwise a doc still
    // holding the PREVIOUS language (a valid locale, so the owner check alone does
    // not help) would be adopted on the next boot and undo this switch.
    localStorage.setItem(CS_UI_LANGUAGE_UNCONFIRMED_KEY, encodeUnconfirmedChoice(this.auth.user.uid, newLang));
    await this.settings.saveAsync();

    // Brief feedback while the reload teardown runs.
    const applying = this.loadingCtrl.create({ content: LL.settings.applyingLanguage() });
    void applying.present();

    // reloadAppAsync NEVER returns — it always reaches location.reload().
    await getMyApp().reloadAppAsync({ resetToRoot: true });
  }



}
